import { EnvironmentInjector, inject, Injectable, NgZone, runInInjectionContext } from '@angular/core';
import type { QueryConstraint, QueryDocumentSnapshot } from '@angular/fire/firestore';
import {
  addDoc,
  collection,
  doc,
  Firestore,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';

import { toAppError } from '../../../shared/utils/app-error.util';
import { FIRESTORE_PATHS } from '../../config/firestore-paths';
import { translateNow } from '../i18n/translate-now';

export interface RequestLogEntry {
  method: string;
  url: string;
  /** `'nexus'` only appears on records logged before Firebase auth moved to Nick Fury. */
  backend: 'coulson' | 'fury' | 'sentinel' | 'nexus' | 'other';
  statusCode: number;
  ok: boolean;
  responseTimeMs: number;
  /** Extracted exclusively from the submitted request body — never from the UI selector. */
  clientId: string;
  storeId: string;
  userId: string;
  env: string;
  toolId: string;
  categoryCode: string;
  requestPayload: unknown;
  responseBody: unknown;
}

export type RequestFeedback = 'pending' | 'successful' | 'failed';

export interface RequestLogRecord extends RequestLogEntry {
  id: string;
  createdAt: Date;
  feedback: RequestFeedback;
  feedbackComment: string;
  feedbackBy: string;
  feedbackAt: Date | null;
}

export interface RequestLogPage {
  records: RequestLogRecord[];
  lastDocument: QueryDocumentSnapshot | null;
}

/**
 * Persists every mutating request Fury UI makes to Coulson/Fury, so we can see which clients
 * get the most config changes and, eventually, train a prediction model on it. Written by
 * request-log.interceptor.ts (one entry per HTTP call) and read back by RequestLogComponent
 * (features/request-log), the "Registro de Peticiones" tool.
 */
@Injectable({ providedIn: 'root' })
export class RequestLogService {
  private readonly db = inject(Firestore, { optional: true });
  private readonly ngZone = inject(NgZone);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private warnedAboutFailure = false;

  /**
   * Fire-and-forget by design: called from an HTTP interceptor's tap/catchError, so a logging
   * failure (offline, quota, rules) must never throw into the real request or reach the user.
   */
  log(entry: RequestLogEntry): void {
    const db = this.db;
    if (!db) return;

    try {
      this.persist(db, entry).catch((error: unknown) => this.warnOnce(error));
    } catch (error) {
      this.warnOnce(error);
    }
  }

  /** Writes one entry; split out from {@link log} so a failing write can be simulated in specs. */
  protected persist(db: Firestore, entry: RequestLogEntry): Promise<unknown> {
    return addDoc(collection(db, FIRESTORE_PATHS.REQUEST_LOG), {
      ...entry,
      created_at: serverTimestamp(),
      // Persist the initial state so feedback tabs can be filtered by Firestore.
      feedback: 'pending' satisfies RequestFeedback,
    });
  }

  /**
   * Never user-facing (see the fire-and-forget note on {@link log}), but not invisible either:
   * warns once so a broken log (rules, quota) can be diagnosed without flooding the console.
   */
  private warnOnce(error: unknown): void {
    if (this.warnedAboutFailure) return;
    this.warnedAboutFailure = true;
    console.warn(
      '[RequestLog] could not persist a request log entry; later failures are not logged.',
      toAppError(error, { source: 'firestore' }),
    );
  }

  /**
   * Most recent entries, newest first. Throws on failure — callers set their own loadError.
   *
   * @param ownerEmail - When set, scopes the query to that user's own entries (`userId ==`)
   * instead of the full collection — how non-admins see only their own requests. Admins pass
   * nothing and get everyone's.
   */
  async listPage(
    pageSize: number,
    ownerEmail?: string,
    feedback?: RequestFeedback,
    after?: QueryDocumentSnapshot | null,
  ): Promise<RequestLogPage> {
    if (!this.db) return { records: [], lastDocument: null };

    const constraints: QueryConstraint[] = [];
    if (ownerEmail) constraints.push(where('userId', '==', ownerEmail));
    if (feedback) constraints.push(where('feedback', '==', feedback));
    constraints.push(orderBy('created_at', 'desc'));
    if (after) constraints.push(startAfter(after));
    constraints.push(limit(pageSize));
    const q = query(collection(this.db, FIRESTORE_PATHS.REQUEST_LOG), ...constraints);
    // AngularFire requires calls to run inside both Angular's zone AND an injection context
    // (see https://github.com/angular/angularfire/blob/main/docs/zones.md) — callers here
    // (component effects/async handlers) aren't guaranteed to still be in either by the time
    // this runs. Without both, this can throw NG0203 the first time the resulting render needs
    // to construct a provider (e.g. CDK Overlay/A11yModule) it hasn't needed before.
    const snap = await this.ngZone.run(() =>
      runInInjectionContext(this.environmentInjector, () => getDocs(q)),
    );

    return {
      records: snap.docs.map((d) => {
        const data = d.data() as RequestLogEntry & {
          created_at?: Timestamp;
          feedback?: RequestFeedback;
          feedback_comment?: string;
          feedback_by?: string;
          feedback_at?: Timestamp;
        };
        return {
          ...data,
          id: d.id,
          createdAt: data.created_at?.toDate() ?? new Date(),
          feedback: data.feedback ?? 'pending',
          feedbackComment: data.feedback_comment ?? '',
          feedbackBy: data.feedback_by ?? '',
          feedbackAt: data.feedback_at?.toDate() ?? null,
        };
      }),
      lastDocument: snap.docs.at(-1) ?? null,
    };
  }

  async count(ownerEmail?: string, feedback?: RequestFeedback): Promise<number> {
    if (!this.db) return 0;

    const constraints: QueryConstraint[] = [];
    if (ownerEmail) constraints.push(where('userId', '==', ownerEmail));
    if (feedback) constraints.push(where('feedback', '==', feedback));
    const q = query(collection(this.db, FIRESTORE_PATHS.REQUEST_LOG), ...constraints);
    return (
      await this.ngZone.run(() => runInInjectionContext(this.environmentInjector, () => getCountFromServer(q)))
    ).data().count;
  }

  async saveFeedback(
    id: string,
    feedback: Exclude<RequestFeedback, 'pending'>,
    comment: string,
    userId: string,
  ): Promise<void> {
    if (!this.db) throw new Error(translateNow('requestLog.errors.firestoreMissing'));

    await updateDoc(doc(this.db, FIRESTORE_PATHS.REQUEST_LOG, id), {
      feedback,
      feedback_comment: comment.trim(),
      feedback_by: userId,
      feedback_at: serverTimestamp(),
    });
  }
}
